import { ISiteFeasibilityRepository } from "@/repositories/SiteFeasibilityRepository";
import {
  SiteFeasibilityRecord,
  SiteFeasibilityListResponse,
  SiteFeasibilityFilters,
  CreateSiteFeasibilityRequest,
  UpdateSiteFeasibilityRequest,
  SiteFeasibilityAuditEntry,
  FeasibilityTemplate,
  FeasibilityTemplateListResponse,
  CreateFeasibilityTemplateRequest,
  FeasibilityRecordV2,
  FeasibilityRecordV2ListResponse,
  CreateFeasibilityRecordV2Request,
  UpdateFeasibilityRecordV2Request,
  FeasibilityResponseReport,
  FeasibilityReportListResponse,
  FeasibilityPublicRecord,
  SubmitQuestionnaireRequest,
} from "@/types/siteFeasibility/siteFeasibility.type";

export interface ISiteFeasibilityUseCase {
  listRecords(
    filters?: SiteFeasibilityFilters,
  ): Promise<SiteFeasibilityListResponse>;
  getRecord(id: string): Promise<SiteFeasibilityRecord>;
  createRecord(
    data: CreateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord>;
  updateRecord(
    id: string,
    data: UpdateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord>;
  deleteRecord(id: string): Promise<void>;
  uploadDocument(id: string, file: File): Promise<SiteFeasibilityRecord>;
  removeDocument(id: string): Promise<SiteFeasibilityRecord>;
  getAuditTrail(id: string): Promise<SiteFeasibilityAuditEntry[]>;

  // V2: Templates
  listTemplates(params?: {
    studyId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityTemplateListResponse>;
  getTemplate(id: string): Promise<FeasibilityTemplate>;
  createTemplate(
    data: CreateFeasibilityTemplateRequest,
  ): Promise<FeasibilityTemplate>;
  updateTemplate(
    id: string,
    data: Partial<CreateFeasibilityTemplateRequest>,
  ): Promise<FeasibilityTemplate>;
  activateTemplate(id: string): Promise<FeasibilityTemplate>;
  inactivateTemplate(id: string): Promise<FeasibilityTemplate>;
  cloneTemplate(id: string): Promise<FeasibilityTemplate>;
  newTemplateVersion(id: string): Promise<FeasibilityTemplate>;
  deleteTemplate(id: string): Promise<void>;

  // V2: Feasibility Records
  listRecordsV2(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
    eligible?: boolean;
    activationReady?: boolean;
  }): Promise<FeasibilityRecordV2ListResponse>;
  getRecordV2(id: string): Promise<FeasibilityRecordV2>;
  createRecordV2(
    data: CreateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2>;
  updateRecordV2(
    id: string,
    data: UpdateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2>;
  sendEmail(id: string): Promise<FeasibilityRecordV2>;
  uploadFeasibilityDocument(
    id: string,
    file: File,
  ): Promise<FeasibilityRecordV2>;
  replaceDocument(id: string, file: File): Promise<FeasibilityRecordV2>;
  replaceVersion(id: string): Promise<FeasibilityRecordV2>;
  markEligibleV2(id: string): Promise<FeasibilityRecordV2>;
  markNotEligibleV2(id: string): Promise<FeasibilityRecordV2>;
  fileToETMF(id: string): Promise<FeasibilityRecordV2>;
  generateReport(id: string): Promise<FeasibilityRecordV2>;
  deleteRecordV2(id: string): Promise<void>;

  // V2: Response Reports
  listResponseReports(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityReportListResponse>;
  getResponseReport(id: string): Promise<FeasibilityResponseReport>;
  fileReportToETMF(id: string, file?: File): Promise<FeasibilityResponseReport>;
  replaceReportVersion(
    id: string,
    file: File,
    reason: string,
  ): Promise<FeasibilityResponseReport>;

  // Public (unauthenticated) — PI-facing questionnaire
  getPublicRecord(recordId: string): Promise<FeasibilityPublicRecord>;
  submitQuestionnaire(
    recordId: string,
    data: SubmitQuestionnaireRequest,
  ): Promise<FeasibilityRecordV2>;
}

export class SiteFeasibilityUseCase implements ISiteFeasibilityUseCase {
  constructor(private readonly repository: ISiteFeasibilityRepository) {}

  async listRecords(
    filters?: SiteFeasibilityFilters,
  ): Promise<SiteFeasibilityListResponse> {
    try {
      return await this.repository.list(filters);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to load site feasibility records",
      );
    }
  }

  async getRecord(id: string): Promise<SiteFeasibilityRecord> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.getById(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch site feasibility record",
      );
    }
  }

  async createRecord(
    data: CreateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord> {
    if (!data.studyId) {
      throw new Error("Study ID is required");
    }
    if (!data.siteId) {
      throw new Error("Site ID is required");
    }
    try {
      return await this.repository.create(data);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create site feasibility record",
      );
    }
  }

  async updateRecord(
    id: string,
    data: UpdateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    // If marking not eligible, clear site number
    if (data.siteEligible === "No") {
      data.siteNumber = "";
    }
    try {
      return await this.repository.update(id, data);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update site feasibility record",
      );
    }
  }

  async deleteRecord(id: string): Promise<void> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.delete(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to delete record",
      );
    }
  }

  async uploadDocument(id: string, file: File): Promise<SiteFeasibilityRecord> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    if (!file) {
      throw new Error("File is required");
    }
    const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
    if (file.size > MAX_SIZE) {
      throw new Error("File size must not exceed 20 MB");
    }
    try {
      return await this.repository.uploadDocument(id, file);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to upload document",
      );
    }
  }

  async removeDocument(id: string): Promise<SiteFeasibilityRecord> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.removeDocument(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to remove document",
      );
    }
  }

  async getAuditTrail(id: string): Promise<SiteFeasibilityAuditEntry[]> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.getAuditTrail(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to fetch audit trail",
      );
    }
  }

  // ─── V2: Templates ──────────────────────────────────────────────────────────

  async listTemplates(params?: {
    studyId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityTemplateListResponse> {
    try {
      return await this.repository.listTemplates(params);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to load templates",
      );
    }
  }

  async getTemplate(id: string): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.getTemplate(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to fetch template",
      );
    }
  }

  async createTemplate(
    data: CreateFeasibilityTemplateRequest,
  ): Promise<FeasibilityTemplate> {
    if (!data.templateName?.trim()) {
      throw new Error("Template name is required");
    }
    try {
      return await this.repository.createTemplate(data);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to create template",
      );
    }
  }

  async updateTemplate(
    id: string,
    data: Partial<CreateFeasibilityTemplateRequest>,
  ): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.updateTemplate(id, data);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to update template",
      );
    }
  }

  async activateTemplate(id: string): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.activateTemplate(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to activate template",
      );
    }
  }

  async inactivateTemplate(id: string): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.inactivateTemplate(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to inactivate template",
      );
    }
  }

  async cloneTemplate(id: string): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.cloneTemplate(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to clone template",
      );
    }
  }

  async newTemplateVersion(id: string): Promise<FeasibilityTemplate> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      return await this.repository.newTemplateVersion(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create new template version",
      );
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    if (!id) {
      throw new Error("Template ID is required");
    }
    try {
      await this.repository.deleteTemplate(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to delete template",
      );
    }
  }

  // ─── V2: Feasibility Records ────────────────────────────────────────────────

  async listRecordsV2(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
    eligible?: boolean;
    activationReady?: boolean;
  }): Promise<FeasibilityRecordV2ListResponse> {
    try {
      return await this.repository.listRecordsV2(params);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to load feasibility records",
      );
    }
  }

  async getRecordV2(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.getRecordV2(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch feasibility record",
      );
    }
  }

  async createRecordV2(
    data: CreateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2> {
    if (!data.studyId) {
      throw new Error("Study ID is required");
    }
    if (!data.siteId) {
      throw new Error("Site ID is required");
    }
    if (!data.mode) {
      throw new Error("Mode is required");
    }
    try {
      return await this.repository.createRecordV2(data);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create feasibility record",
      );
    }
  }

  async updateRecordV2(
    id: string,
    data: UpdateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.updateRecordV2(id, data);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update feasibility record",
      );
    }
  }

  async sendEmail(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.sendEmail(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to send email",
      );
    }
  }

  async uploadFeasibilityDocument(
    id: string,
    file: File,
  ): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error("File size must not exceed 20 MB");
    }
    try {
      return await this.repository.uploadFeasibilityDocument(id, file);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to upload document",
      );
    }
  }

  async replaceDocument(id: string, file: File): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error("File size must not exceed 20 MB");
    }
    try {
      return await this.repository.replaceDocument(id, file);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to replace document",
      );
    }
  }

  async replaceVersion(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.replaceVersion(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to replace version",
      );
    }
  }

  async markEligibleV2(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.markEligibleV2(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to mark as eligible",
      );
    }
  }

  async markNotEligibleV2(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.markNotEligibleV2(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to mark as not eligible",
      );
    }
  }

  async fileToETMF(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.fileToETMF(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to file to eTMF",
      );
    }
  }

  async generateReport(id: string): Promise<FeasibilityRecordV2> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.generateReport(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to generate report",
      );
    }
  }

  async deleteRecordV2(id: string): Promise<void> {
    if (!id) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.deleteRecordV2(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to delete record",
      );
    }
  }

  // ─── V2: Response Reports ───────────────────────────────────────────────────

  async listResponseReports(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityReportListResponse> {
    try {
      return await this.repository.listResponseReports(params);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to load response reports",
      );
    }
  }

  async getResponseReport(id: string): Promise<FeasibilityResponseReport> {
    if (!id) {
      throw new Error("Report ID is required");
    }
    try {
      return await this.repository.getResponseReport(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch response report",
      );
    }
  }

  async fileReportToETMF(
    id: string,
    file?: File,
  ): Promise<FeasibilityResponseReport> {
    if (!id) {
      throw new Error("Report ID is required");
    }
    try {
      return await this.repository.fileReportToETMF(id, file);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to file report to eTMF",
      );
    }
  }

  async replaceReportVersion(
    id: string,
    file: File,
    reason: string,
  ): Promise<FeasibilityResponseReport> {
    if (!id) {
      throw new Error("Report ID is required");
    }
    if (!file) {
      throw new Error("File is required");
    }
    if (!reason?.trim()) {
      throw new Error("Reason is required");
    }
    try {
      return await this.repository.replaceReportVersion(id, file, reason);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to replace report version",
      );
    }
  }

  async getPublicRecord(recordId: string): Promise<FeasibilityPublicRecord> {
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    try {
      return await this.repository.getPublicRecord(recordId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch questionnaire",
      );
    }
  }

  async submitQuestionnaire(
    recordId: string,
    data: SubmitQuestionnaireRequest,
  ): Promise<FeasibilityRecordV2> {
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    if (!data.responses || data.responses.length === 0) {
      throw new Error("At least one response is required");
    }
    try {
      return await this.repository.submitQuestionnaire(recordId, data);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to submit questionnaire",
      );
    }
  }
}
